import { createContext, useContext } from "react";
import type { GameClient } from "@game/services";
import type { Me, OwnedDigimon, Wallet, WorldPosition } from "@game/types";

export interface GameSession {
  /** Profile from /me. */
  me: Me;
  /** /world/position result. */
  position: WorldPosition;
  /** Owned digimons (party + storage). */
  digimons: OwnedDigimon[];
  /** Latest wallet snapshot. Refreshed after currency-impacting actions. */
  wallet: Wallet;
}

export type GameStage =
  | { kind: "boot" }
  | { kind: "login" }
  | { kind: "register" }
  | { kind: "verify-email"; pendingEmail: string }
  | { kind: "onboarding"; characterId: string }
  | { kind: "world"; session: GameSession }
  | { kind: "battle"; session: GameSession; roomId: string; mode: "pve" | "pvp_casual" | "pvp_ranked" }
  | { kind: "error"; message: string; retryable: boolean };

export interface GameContextValue {
  client: GameClient;
  stage: GameStage;
  setStage: (s: GameStage) => void;
  /** Force re-fetch of /me + /digimons + /wallet. */
  refreshSession: () => Promise<void>;
}

export const GameContext = createContext<GameContextValue | null>(null);

export const useGame = (): GameContextValue => {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used inside <GameProvider>");
  return ctx;
};
